<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import QuoteBreakdown from '../QuoteBreakdown.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));
const form = computed(() => session.value.form);
const uiFlags = computed(() => carBooking.uiFlags);
const quote = computed(() => session.value.quote);

const update = patch => carBooking.setForm(props.conversationId, patch);

const outcome = ref(null);
const unavailable = ref(false);
const errorMessage = ref(null);
const equipmentOptions = ref([]);

const outcomeMessage = computed(() => {
  if (outcome.value?.outcome === 'price_changed') {
    return t('CONVERSATION.CAR_BOOKING.BOOK.PRICE_CHANGED', {
      old: outcome.value.old,
      new: outcome.value.new,
    });
  }
  if (outcome.value?.outcome === 'unavailable') {
    return t('CONVERSATION.CAR_BOOKING.BOOK.UNAVAILABLE');
  }
  return '';
});

const vehicle = computed(() => quote.value?.vehicle || {});
const insurances = computed(() => quote.value?.insurances || []);

// Booking currency (what the customer is charged in). Each Money carries its own
// explicit bookingCurrency code (authoritative); fall back to the quote-level
// booking currency and finally the line's own currency for older responses.
const bookingCurrency = computed(
  () =>
    quote.value?.totalPrice?.bookingCurrency ||
    quote.value?.totalPrice?.currency
);

const formatPrice = price => {
  if (!price) return '';
  const amount = price.inBookingCurrency ?? price.amount;
  const code = price.bookingCurrency || bookingCurrency.value || price.currency;
  return `${amount} ${code}`;
};

const isInsuranceSelected = code => session.value.insurances.includes(code);

const requote = () =>
  carBooking.quote(props.conversationId, {
    token: session.value.selected,
    insurances: session.value.insurances,
    equipment: session.value.equipment,
  });

const runQuote = async () => {
  outcome.value = null;
  errorMessage.value = null;
  const result = await requote();
  unavailable.value = result?.outcome === 'unavailable';
  if (result?.error !== undefined) {
    errorMessage.value =
      result.error || t('CONVERSATION.CAR_BOOKING.BOOK.QUOTE_ERROR');
  }
};

const toggleInsurance = code => {
  const next = isInsuranceSelected(code)
    ? session.value.insurances.filter(item => item !== code)
    : [...session.value.insurances, code];
  carBooking.setInsurances(props.conversationId, next);
  runQuote();
};

const equipmentQty = code => {
  const item = session.value.equipment.find(entry => entry.code === code);
  return item ? item.qty : 0;
};

const setEquipmentQty = (code, qty) => {
  const others = session.value.equipment.filter(entry => entry.code !== code);
  const next = qty > 0 ? [...others, { code, qty }] : others;
  carBooking.setEquipment(props.conversationId, next);
  runQuote();
};

const loadEquipment = async () => {
  try {
    const { pickupDate, pickupTime, dropoffDate, dropoffTime } = form.value;
    const items = await carBooking.fetchEquipment(props.conversationId, {
      location: form.value.pickupLocationCode,
      from: `${pickupDate}T${pickupTime}:00Z`,
      to: `${dropoffDate}T${dropoffTime}:00Z`,
    });
    equipmentOptions.value = items || [];
  } catch (error) {
    equipmentOptions.value = [];
  }
};

onMounted(() => {
  runQuote();
  loadEquipment();
});

const canConfirm = computed(
  () =>
    form.value.driverName &&
    form.value.email &&
    session.value.selected &&
    quote.value &&
    !errorMessage.value
);

// The gateway's BookDriverDto requires firstName/lastName/countryOfResidence;
// split the single name field and carry over the country + age from the trip form.
const buildDriver = () => {
  const parts = (form.value.driverName || '').trim().split(/\s+/);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ') || firstName;
  return {
    firstName,
    lastName,
    countryOfResidence: form.value.countryOfResidence,
    age: form.value.driverAge ? Number(form.value.driverAge) : undefined,
    email: form.value.email,
  };
};

const confirm = async () => {
  outcome.value = null;
  const result = await carBooking.book(props.conversationId, {
    token: session.value.selected,
    driver: buildDriver(),
    insurances: session.value.insurances,
    equipment: session.value.equipment,
  });
  if (result?.outcome) outcome.value = result;
};

const research = () => carBooking.setStep(props.conversationId, 'trip');

const back = () => carBooking.setStep(props.conversationId, 'choice');
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-1 flex-col gap-4 overflow-y-auto">
      <div
        v-if="uiFlags.quoting && !quote"
        class="flex flex-col items-center gap-2 py-8 text-n-slate-11"
      >
        <Spinner :size="24" />
        <p class="m-0 text-sm">
          {{ t('CONVERSATION.CAR_BOOKING.BOOK.QUOTING') }}
        </p>
      </div>

      <div
        v-else-if="unavailable"
        class="flex flex-col gap-2 rounded-lg border border-n-amber-8 bg-n-amber-9/10 px-3 py-2.5"
      >
        <p class="m-0 text-sm text-n-slate-12">
          {{ t('CONVERSATION.CAR_BOOKING.BOOK.UNAVAILABLE') }}
        </p>
        <NextButton
          faded
          amber
          sm
          :label="t('CONVERSATION.CAR_BOOKING.BOOK.RESEARCH')"
          @click="research"
        />
      </div>

      <template v-else>
        <div v-if="quote" class="flex flex-col gap-4">
          <div
            class="flex flex-col gap-0.5 rounded-xl border border-n-weak bg-n-surface-1 p-3.5"
          >
            <span class="text-sm font-semibold text-n-slate-12">
              {{ vehicle.sampleModel || vehicle.category }}
            </span>
            <span v-if="vehicle.category" class="text-xs text-n-slate-11">
              {{ vehicle.category }}
            </span>
          </div>

          <QuoteBreakdown :quote="quote" :show-insurances="false" />

          <div v-if="insurances.length" class="flex flex-col gap-1.5">
            <p
              class="m-0 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('CONVERSATION.CAR_BOOKING.BOOK.INSURANCE') }}
            </p>
            <div
              v-for="insurance in insurances"
              :key="insurance.code"
              class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2.5"
            >
              <div class="flex min-w-0 flex-col gap-0.5">
                <span class="truncate text-sm text-n-slate-12">
                  {{ insurance.name }}
                </span>
                <span v-if="insurance.excess" class="text-xs text-n-slate-11">
                  {{
                    t('CONVERSATION.CAR_BOOKING.BOOK.EXCESS', {
                      amount: formatPrice(insurance.excess),
                    })
                  }}
                </span>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <span
                  v-if="insurance.price && insurance.type !== 'INCLUDED'"
                  class="text-sm text-n-slate-11"
                >
                  {{ formatPrice(insurance.price) }}
                </span>
                <span
                  v-if="insurance.type === 'INCLUDED'"
                  class="text-xs font-medium text-n-green-11"
                >
                  {{ t('CONVERSATION.CAR_BOOKING.BOOK.INSURANCE_INCLUDED') }}
                </span>
                <span
                  v-else-if="insurance.type === 'MANDATORY'"
                  class="text-xs font-medium text-n-slate-11"
                >
                  {{ t('CONVERSATION.CAR_BOOKING.BOOK.INSURANCE_MANDATORY') }}
                </span>
                <input
                  v-else
                  type="checkbox"
                  :checked="isInsuranceSelected(insurance.code)"
                  :disabled="uiFlags.quoting"
                  class="size-4 cursor-pointer rounded border-n-weak text-n-brand"
                  @change="toggleInsurance(insurance.code)"
                />
              </div>
            </div>
          </div>

          <div v-if="equipmentOptions.length" class="flex flex-col gap-1.5">
            <p
              class="m-0 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('CONVERSATION.CAR_BOOKING.BOOK.EQUIPMENT') }}
            </p>
            <div
              v-for="item in equipmentOptions"
              :key="item.code"
              class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2.5"
            >
              <span class="min-w-0 truncate text-sm text-n-slate-12">
                {{ item.name }}
              </span>
              <div class="flex shrink-0 items-center gap-2">
                <NextButton
                  faded
                  slate
                  xs
                  icon="i-lucide-minus"
                  :disabled="uiFlags.quoting || equipmentQty(item.code) <= 0"
                  @click="
                    setEquipmentQty(item.code, equipmentQty(item.code) - 1)
                  "
                />
                <span class="w-4 text-center text-sm text-n-slate-12">
                  {{ equipmentQty(item.code) }}
                </span>
                <NextButton
                  faded
                  slate
                  xs
                  icon="i-lucide-plus"
                  :disabled="uiFlags.quoting || equipmentQty(item.code) >= 3"
                  @click="
                    setEquipmentQty(item.code, equipmentQty(item.code) + 1)
                  "
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Not chained to the quote block: a failed re-quote (insurance/equipment
             toggle) leaves the previous breakdown rendered, so the error must show
             alongside it rather than instead of it. -->
        <div
          v-if="errorMessage"
          class="rounded-lg border border-n-ruby-8 bg-n-ruby-9/10 px-3 py-2.5 text-sm text-n-slate-12"
        >
          {{ errorMessage }}
        </div>

        <div class="flex flex-col gap-1.5">
          <p
            class="m-0 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CONVERSATION.CAR_BOOKING.BOOK.DRIVER_DETAILS') }}
          </p>
          <NextInput
            :model-value="form.driverName"
            :label="t('CONVERSATION.CAR_BOOKING.BOOK.DRIVER_NAME')"
            :placeholder="
              t('CONVERSATION.CAR_BOOKING.BOOK.DRIVER_NAME_PLACEHOLDER')
            "
            @update:model-value="update({ driverName: $event })"
          />
          <NextInput
            :model-value="form.email"
            type="email"
            :label="t('CONVERSATION.CAR_BOOKING.BOOK.EMAIL')"
            :placeholder="t('CONVERSATION.CAR_BOOKING.BOOK.EMAIL_PLACEHOLDER')"
            @update:model-value="update({ email: $event })"
          />
        </div>

        <div
          v-if="outcomeMessage"
          class="flex flex-col gap-2 rounded-lg border border-n-amber-8 bg-n-amber-9/10 px-3 py-2.5"
        >
          <p class="m-0 text-sm text-n-slate-12">{{ outcomeMessage }}</p>
          <NextButton
            faded
            amber
            sm
            :label="t('CONVERSATION.CAR_BOOKING.BOOK.RESEARCH')"
            @click="research"
          />
        </div>
      </template>
    </div>

    <div
      v-if="!(uiFlags.quoting && !quote) && !unavailable"
      class="mt-4 flex items-center gap-3 border-t border-n-weak bg-n-surface-2 px-4 py-3"
    >
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CAR_BOOKING.BOOK.BACK')"
        @click="back"
      />
      <NextButton
        solid
        blue
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.BOOK.CONFIRM')"
        :disabled="!canConfirm || uiFlags.booking || uiFlags.quoting"
        :is-loading="uiFlags.booking"
        @click="confirm"
      />
    </div>
  </div>
</template>
